import clientModule from "braintree-web/client";
import hostedFieldsModule from "braintree-web/hosted-fields";
import unionpayModule from "braintree-web/unionpay";

// braintree-web submodules are CommonJS; guard against ESM interop wrapping the
// factory under `.default` so `.create` is always reachable.
const braintreeClient = clientModule.create
    ? clientModule
    : clientModule.default;
const braintreeHostedFields = hostedFieldsModule.create
    ? hostedFieldsModule
    : hostedFieldsModule.default;
const braintreeUnionpay = unionpayModule.create
    ? unionpayModule
    : unionpayModule.default;

/**
 * Braintree signals a missing expiration date / CVV in a few different shapes
 * depending on whether the client SDK or the gateway rejected the request.
 */
function requiresCardDetails(error) {
    const haystack = [
        error?.code,
        error?.message,
        error?.details?.originalError?.message,
        JSON.stringify(error?.details?.originalError?.fieldErrors || ""),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    return (
        haystack.includes("expiration") ||
        haystack.includes("cvv") ||
        haystack.includes("81709") ||
        haystack.includes("81707") ||
        haystack.includes("81706")
    );
}

export function registerBraintreeCheckout() {
    window.Alpine.data("braintreeCheckout", (config = {}) => {
        return {
            clientToken: config.clientToken,
            messages: config.messages || {},

            hostedFields: null,
            unionpay: null,
            checkoutReady: false,
            isProcessing: false,
            errorMessage: "",

            // Expiration date and CVV stay off-screen unless the card turns out
            // to need them. Braintree's docs are explicit that UnionPay debit
            // cards "do not always require CVV or expiration date" — so some
            // real debit cards do. The fields must exist at create() time
            // because Hosted Fields cannot add fields to a live instance.
            showCardDetails: false,

            // UnionPay enrollment state
            mobileCountryCode: "86",
            mobileNumber: "",
            enrollmentId: null,
            smsCodeRequired: false,
            showSmsCode: false,
            smsCode: "",

            async init() {
                try {
                    const clientInstance = await braintreeClient.create({
                        authorization: this.clientToken,
                    });

                    this.hostedFields = await braintreeHostedFields.create({
                        client: clientInstance,
                        styles: {
                            input: {
                                "font-size": "16px",
                                "font-family":
                                    "Inter, ui-sans-serif, system-ui, sans-serif",
                                color: "#111827",
                            },
                            ":focus": { color: "#111827" },
                            ".invalid": { color: "#dc2626" },
                        },
                        fields: {
                            number: {
                                container: "#bt-card-number",
                                placeholder: "•••• •••• •••• ••••",
                            },
                            expirationDate: {
                                container: "#bt-expiration",
                                placeholder: "MM/YY",
                            },
                            cvv: {
                                container: "#bt-cvv",
                                placeholder: "•••",
                            },
                        },
                    });

                    this.unionpay = await braintreeUnionpay.create({
                        client: clientInstance,
                    });

                    this.checkoutReady = true;
                } catch (error) {
                    console.error("Braintree initialization error:", error);
                    this.errorMessage =
                        this.messages.initError || this.messages.genericError;
                }
            },

            /**
             * Reveal the expiration/CVV fields and ask the customer to complete
             * them. Returns true so callers can bail out of the current attempt.
             */
            revealCardDetails() {
                this.showCardDetails = true;
                this.errorMessage = this.messages.cardDetailsRequired;

                return true;
            },

            async handleSubmit() {
                if (this.isProcessing || !this.checkoutReady) {
                    return;
                }

                this.isProcessing = true;
                this.errorMessage = "";

                try {
                    // 1) Enforce "UnionPay debit only".
                    const capabilities = await this.unionpay.fetchCapabilities({
                        hostedFields: this.hostedFields,
                    });

                    if (
                        !capabilities.isUnionPay ||
                        !(capabilities.unionPay && capabilities.unionPay.isSupported)
                    ) {
                        this.errorMessage = this.messages.unionpayOnly;

                        return;
                    }

                    if (!capabilities.isDebit) {
                        this.errorMessage = this.messages.debitOnly;

                        return;
                    }

                    // 2) Enroll the card. May require an SMS code, and may turn
                    //    out to need the expiration date after all.
                    if (!this.enrollmentId) {
                        let enrollment;

                        try {
                            enrollment = await this.unionpay.enroll({
                                hostedFields: this.hostedFields,
                                mobile: {
                                    countryCode: this.mobileCountryCode,
                                    number: this.mobileNumber,
                                },
                            });
                        } catch (error) {
                            if (!this.showCardDetails && requiresCardDetails(error)) {
                                this.revealCardDetails();

                                return;
                            }

                            throw error;
                        }

                        this.enrollmentId = enrollment.enrollmentId;
                        this.smsCodeRequired = enrollment.smsCodeRequired;

                        if (this.smsCodeRequired) {
                            this.showSmsCode = true;

                            return;
                        }
                    }

                    // 3) Tokenize with the enrollment id and charge server-side.
                    let payload;

                    try {
                        payload = await this.unionpay.tokenize({
                            hostedFields: this.hostedFields,
                            enrollmentId: this.enrollmentId,
                            smsCode: this.smsCodeRequired ? this.smsCode : undefined,
                        });
                    } catch (error) {
                        if (!this.showCardDetails && requiresCardDetails(error)) {
                            this.revealCardDetails();

                            return;
                        }

                        throw error;
                    }

                    await this.$wire.confirmPayment(payload.nonce);
                } catch (error) {
                    console.error(error);
                    this.errorMessage = this.messages.genericError;
                } finally {
                    this.isProcessing = false;
                }
            },
        };
    });
}
